import React, { useContext } from 'react';
import {
  ChatReferenceDto,
  ChatReferenceWithParsedBookContentProps,
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { FlexItem } from '../../components/flex/FlexItem.component';
import {
  getBookTitle,
  getBreadcrumbsDisplayParts,
  getCitationDisplayNumberMap,
  getOrderedCitations,
  getStartPageNumber,
  isCitationClickable,
  isSamePageCitation
} from './ai-chat.utilities';
import { AppContext } from '../../components/app/AppProvider.component';
import cx from 'classnames';

export type AiChatEntryCitationProps = {
  citations: ChatReferenceDto[];
  handleClick: (citation: ChatReferenceDto) => void;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const AiChatEntryCitation = (props: AiChatEntryCitationProps): JSX.Element => {

  const {
    appState,
  } = useContext(AppContext);

  const {
    citations,
    handleClick,
  } = props;

  if (!citations || !citations.length) {
    return null;
  }

  const renderBookLink = (content: string, citation: ChatReferenceWithParsedBookContentProps) => {

    const isClickable = isCitationClickable(appState, citation);

    return (
      <button
        type="button"
        role={isClickable ? "link" : null}
        className={cx("u-els-debuttonize", {
          'u-els-color-n7 u-naa-textualize': !isClickable,
          'u-els-anchorize': isClickable,
        })}
        aria-disabled={!isClickable}
        onClick={() => {
          handleClick(citation);
        }}
      >
        {content}
      </button>
    );
  };

  const orderedCitations = getOrderedCitations(citations);
  // Raw index -> 1-based display position, matching the inline pill numbering
  // (gaps from hidden, un-cited sources are not shown to the user).
  const citationDisplayNumbers = getCitationDisplayNumberMap(citations);

  // Citations sharing a page/chapter/breadcrumb path are distinct cited passages, not
  // duplicates (see isSamePageCitation) - group them so the panel can label each with
  // its position in the group (e.g. "(1 of 2)"), making clear the repeated page number
  // isn't an error.
  const samePageGroups = orderedCitations.map((citation) => {
    return orderedCitations.filter((other) => isSamePageCitation(citation, other));
  });

  return (
    <ol className="c-els-ai-chat__entry-reference-list">
      {
        orderedCitations.map((citation, citationIdx) => {
          const parts = getBreadcrumbsDisplayParts(citation?._contentProps?.bread_crumbs);
          // Headline is the deep-linkable section breadcrumb; when a citation has no
          // section under its chapter, the chapter title becomes the headline link.
          // Some chunks (e.g. reference tables) carry no breadcrumbs at all - fall back
          // to the chunk's own title, then the book title, so the citation still renders
          // a clickable link instead of disappearing entirely.
          const headline = parts.sections || parts.chapter
            || citation._contentProps.document_title
            || citation.resourceTitle;
          // The "Ch NN: <chapter title>" line only shows when the chapter title is
          // not already serving as the headline (i.e. sections exist above it).
          const chapterNumber = citation._contentProps.chapter_number;
          const showChapterLine = parts.sections && (chapterNumber || parts.chapter);

          const pageNumber = getStartPageNumber(citation);
          const samePageGroup = samePageGroups[citationIdx];
          const samePageSuffix = samePageGroup.length > 1
            ? ` (${samePageGroup.indexOf(citation) + 1} of ${samePageGroup.length})`
            : '';
          const bookTitle = getBookTitle(citation._contentProps.isbn, appState) || citation.resourceTitle;
          const bookLine = [bookTitle, pageNumber ? `p ${pageNumber}${samePageSuffix}` : null]
            .filter(Boolean)
            .join(', ');

          return (
            <li
              key={citation.id}
              id={`chat-source-${citation.entryId}-${citation.index}`}
              className="c-els-ai-chat__entry-reference"
            >
              <FlexLayout modifiers={[
                FlexLayoutModifier.GUTTERS_1o2,
                FlexLayoutModifier.LEFT,
                FlexLayoutModifier.TOP
              ]}>
                <FlexItem>
                  <span
                    className="c-els-pill c-els-pill--citation"
                    aria-hidden="true"
                  >
                    {citationDisplayNumbers.get(citation.index)}
                  </span>
                </FlexItem>
                <FlexItem classes={['u-cw-min-width-0']}>
                  {headline && (
                    <div className="u-els-margin-bottom-1o4">
                      {renderBookLink(headline, citation)}
                    </div>
                  )}
                  {showChapterLine && (
                    <FlexLayout
                      classes={['c-els-ai-chat__entry-reference-chapter']}
                      modifiers={[
                        FlexLayoutModifier.LEFT,
                        FlexLayoutModifier.GUTTERS,
                      ]}
                    >
                      {chapterNumber && (
                        <FlexItem classes={['c-els-ai-chat__entry-reference-chapter-number', 'u-els-color-n7']}>
                          {`Ch ${chapterNumber}:`}
                        </FlexItem>
                      )}
                      {parts.chapter && (
                        <FlexItem classes={['c-els-ai-chat__entry-reference-chapter-title']}>
                          {parts.chapter}
                        </FlexItem>
                      )}
                    </FlexLayout>
                  )}
                  {bookLine && (
                    <div className="c-els-ai-chat__entry-reference-book">{bookLine}</div>
                  )}
                </FlexItem>
              </FlexLayout>
            </li>
          );
        })
      }
    </ol>
  );

};
